import { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger
} from "../../components/ui/dialog";
import { Pencil, Trash2, Plus, Upload, X, Loader2, Video, Link as LinkIcon, Trash } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { fmt } from "../../lib/money";
import ProductVideo, { detectVideoKind } from "../../components/ProductVideo";
import SellPriceCalculator from "../../components/SellPriceCalculator";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (u) => (u?.startsWith("http") ? u : `${BACKEND}${u}`);
const empty = { title: "", description: "", price: "", stock: "", category: "", images: [], sizes: [], colors: [], video_url: "", affiliate_commission_pct: "", external_link: "", external_link_label: "" };
const MAX_IMAGES = 8;
const MAX_MB = 10;
const MAX_VIDEO_MB = 200;

export default function SellerProducts() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [colorName, setColorName] = useState("");
  const [colorHex, setColorHex] = useState("#000000");
  const [videoUploading, setVideoUploading] = useState(false);
  const fileRef = useRef(null);
  const videoFileRef = useRef(null);

  const load = () => {
    api.get("/seller/products").then((r) => setProducts(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      title: p.title, description: p.description,
      price: String(p.price), stock: String(p.stock),
      category: p.category, images: p.images || [],
      sizes: p.sizes || [], colors: p.colors || [],
      video_url: p.video_url || "",
      affiliate_commission_pct: p.affiliate_commission_pct ? String(p.affiliate_commission_pct) : "",
      external_link: p.external_link || "",
      external_link_label: p.external_link_label || "",
    });
    setOpen(true);
  };

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (form.images.length + files.length > MAX_IMAGES) {
      toast.error(`Max ${MAX_IMAGES} images per product`);
      return;
    }
    setUploading(true);
    const uploaded = [];
    for (const f of files) {
      if (f.size > MAX_MB * 1024 * 1024) {
        toast.error(`${f.name} is over ${MAX_MB} MB`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/uploads/image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data.url);
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Upload failed for ${f.name}`);
      }
    }
    if (uploaded.length) {
      setForm((prev) => ({ ...prev, images: [...prev.images, ...uploaded] }));
      toast.success(`Uploaded ${uploaded.length} image${uploaded.length > 1 ? "s" : ""}`);
    }
    setUploading(false);
  };

  const onPickVideo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > MAX_VIDEO_MB * 1024 * 1024) {
      toast.error(`Video over ${MAX_VIDEO_MB} MB`);
      return;
    }
    setVideoUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/uploads/video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({ ...prev, video_url: data.url }));
      toast.success("Video uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setVideoUploading(false);
    }
  };

  const removeImage = (idx) => {
    setForm((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      title: form.title, description: form.description,
      price: parseFloat(form.price), stock: parseInt(form.stock || "0"),
      category: form.category,
      images: form.images,
      sizes: form.sizes,
      colors: form.colors,
      video_url: form.video_url || null,
      affiliate_commission_pct: form.affiliate_commission_pct ? parseFloat(form.affiliate_commission_pct) : 0,
      external_link: form.external_link?.trim() || null,
      external_link_label: form.external_link_label?.trim() || null,
    };
    try {
      if (editing) {
        await api.put(`/products/${editing.product_id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete ${p.title}?`)) return;
    await api.delete(`/products/${p.product_id}`);
    toast.success("Deleted");
    load();
  };

  const canCreate = user?.role === "admin" || (user?.role === "seller" && user?.seller_status === "approved");

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-2 flex-wrap">
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight">Products</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="new-product-btn"
              onClick={openCreate}
              disabled={!canCreate}
              className="rounded-none bg-[#0033FF] hover:bg-[#0022CC] h-11 shrink-0"
            >
              <Plus className="w-4 h-4 mr-2" /> <span className="hidden xs:inline">New product</span><span className="xs:hidden">New</span>
            </Button>
          </DialogTrigger>
          <DialogContent
            className="rounded-none border-black w-screen sm:w-[calc(100%-2rem)] max-w-2xl h-[100dvh] sm:h-auto sm:max-h-[88vh] p-0 gap-0 flex flex-col top-0 left-0 translate-x-0 translate-y-0 sm:top-[50%] sm:left-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%]"
          >
            <DialogHeader className="sticky top-0 z-10 bg-white border-b border-neutral-200 px-4 sm:px-6 py-3 flex-row items-center justify-between space-y-0">
              <div className="text-left min-w-0">
                <DialogTitle className="font-display font-black text-xl sm:text-2xl truncate">
                  {editing ? "Edit product" : "New product"}
                </DialogTitle>
                <DialogDescription className="text-xs text-neutral-500 mt-0.5 hidden sm:block">
                  {editing ? "Update product details and images." : "Create a new product listing."}
                </DialogDescription>
              </div>
              <button
                type="button"
                data-testid="dialog-close-btn"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="w-10 h-10 grid place-items-center border border-black hover:bg-black hover:text-white transition-colors shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </DialogHeader>
            <form id="product-form" onSubmit={submit} className="space-y-4 flex-1 overflow-y-auto px-4 sm:px-6 py-4">
              <div className="space-y-1.5">
                <Label htmlFor="title">Title</Label>
                <Input data-testid="form-title" id="title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none border-black h-11" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="description">Description</Label>
                <Textarea data-testid="form-description" id="description" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none border-black" rows={4} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="price">Price</Label>
                  <Input data-testid="form-price" id="price" required type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="rounded-none border-black h-11" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="stock">Stock</Label>
                  <Input data-testid="form-stock" id="stock" required type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="rounded-none border-black h-11" />
                </div>
                <div className="space-y-1.5 col-span-2 sm:col-span-1">
                  <Label htmlFor="category">Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="form-category" className="rounded-none border-black h-11"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent className="rounded-none border-black">
                      {categories.map((c) => <SelectItem key={c.category_id} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="commission" className="flex items-center gap-2">
                  Affiliate commission %
                  <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Share-to-earn</span>
                </Label>
                <Input
                  data-testid="form-commission"
                  id="commission"
                  type="number" min="0" max="50" step="0.5"
                  value={form.affiliate_commission_pct}
                  onChange={(e) => setForm({ ...form, affiliate_commission_pct: e.target.value })}
                  placeholder="0 — no commission"
                  className="rounded-none border-black h-11"
                />
                <p className="text-xs text-neutral-500">
                  Pays this % of the item price to anyone who shares this product via Trackmatrix and drives a sale. Comes out of your revenue. Set 0 to disable.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Images ({form.images.length} / {MAX_IMAGES})</Label>
                <div className="grid grid-cols-4 gap-2">
                  {form.images.map((src, i) => (
                    <div key={i} className="relative aspect-square border border-neutral-300 bg-neutral-50 overflow-hidden">
                      <img src={resolveImg(src)} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        data-testid={`remove-image-${i}`}
                        onClick={() => removeImage(i)}
                        className="absolute top-1 right-1 w-6 h-6 bg-black text-white grid place-items-center hover:bg-[#FF2A00]"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  {form.images.length < MAX_IMAGES && (
                    <button
                      type="button"
                      data-testid="pick-files-btn"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      className="aspect-square border-2 border-dashed border-neutral-400 hover:border-[#0033FF] hover:text-[#0033FF] grid place-items-center text-xs font-semibold transition-colors disabled:opacity-50"
                    >
                      {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                        <div className="flex flex-col items-center gap-1">
                          <Upload className="w-5 h-5" />
                          <span>Upload</span>
                        </div>
                      )}
                    </button>
                  )}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  multiple
                  onChange={onPickFiles}
                  className="hidden"
                  data-testid="file-input"
                />
                <p className="text-xs text-neutral-500">JPG/PNG/WEBP/GIF, up to {MAX_MB} MB each, max {MAX_IMAGES} images.</p>
              </div>

              {/* Sizes */}
              <div className="space-y-2">
                <Label>Sizes (optional)</Label>
                <div className="flex flex-wrap gap-2">
                  {form.sizes.map((s, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1.5 bg-neutral-100 border border-neutral-300 px-2.5 py-1 text-sm font-semibold"
                      data-testid={`size-chip-${i}`}
                    >
                      {s}
                      <button
                        type="button"
                        data-testid={`remove-size-${i}`}
                        onClick={() => setForm({ ...form, sizes: form.sizes.filter((_, x) => x !== i) })}
                        className="text-neutral-500 hover:text-[#FF2A00]"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    data-testid="size-input"
                    placeholder="e.g. S, M, L, XL or 8, 9, 10"
                    className="rounded-none border-black h-10"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        const v = e.currentTarget.value.trim().replace(/,$/, "");
                        if (v && !form.sizes.includes(v)) setForm({ ...form, sizes: [...form.sizes, v] });
                        e.currentTarget.value = "";
                      }
                    }}
                  />
                  <span className="text-xs text-neutral-500 self-center whitespace-nowrap">Press Enter to add</span>
                </div>
                <div className="flex flex-wrap gap-1 pt-1">
                  {["XS", "S", "M", "L", "XL", "XXL"].map((s) => (
                    <button
                      type="button"
                      key={s}
                      data-testid={`quick-size-${s}`}
                      onClick={() => !form.sizes.includes(s) && setForm({ ...form, sizes: [...form.sizes, s] })}
                      className="text-[11px] uppercase tracking-widest border border-neutral-300 px-2 py-1 hover:bg-black hover:text-white"
                    >
                      + {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Colors */}
              <div className="space-y-2">
                <Label>Colors (optional)</Label>
                <div className="flex flex-wrap gap-2">
                  {form.colors.map((c, i) => (
                    <span
                      key={i}
                      data-testid={`color-chip-${i}`}
                      className="inline-flex items-center gap-2 bg-neutral-100 border border-neutral-300 px-2.5 py-1 text-sm"
                    >
                      <span className="w-4 h-4 border border-neutral-400" style={{ background: c.hex }} />
                      <span className="font-semibold">{c.name}</span>
                      <button
                        type="button"
                        data-testid={`remove-color-${i}`}
                        onClick={() => setForm({ ...form, colors: form.colors.filter((_, x) => x !== i) })}
                        className="text-neutral-500 hover:text-[#FF2A00]"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    data-testid="color-hex-input"
                    value={colorHex}
                    onChange={(e) => setColorHex(e.target.value)}
                    className="w-10 h-10 border border-black cursor-pointer p-0"
                  />
                  <Input
                    data-testid="color-name-input"
                    placeholder="Color name (e.g. Midnight)"
                    value={colorName}
                    onChange={(e) => setColorName(e.target.value)}
                    className="rounded-none border-black h-10 flex-1"
                  />
                  <Button
                    type="button"
                    data-testid="add-color-btn"
                    onClick={() => {
                      const n = colorName.trim();
                      if (!n) { toast.error("Name the color"); return; }
                      if (form.colors.some((c) => c.name.toLowerCase() === n.toLowerCase())) return;
                      setForm({ ...form, colors: [...form.colors, { name: n, hex: colorHex }] });
                      setColorName("");
                    }}
                    className="rounded-none bg-black hover:bg-neutral-800 h-10"
                  >
                    Add
                  </Button>
                </div>
              </div>

              {/* Product video */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Video className="w-4 h-4" /> Product video (optional)
                </Label>
                <p className="text-xs text-neutral-500">
                  Paste a YouTube, Facebook, or TikTok link — <i>or</i> upload your own short video (mp4/webm/mov, max {MAX_VIDEO_MB} MB). One video per product.
                </p>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <LinkIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                    <Input
                      data-testid="video-link-input"
                      value={form.video_url}
                      onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                      placeholder="https://youtube.com/… · facebook.com/… · tiktok.com/…"
                      className="rounded-none border-black h-11 pl-9"
                    />
                  </div>
                  <Button
                    type="button"
                    data-testid="upload-video-btn"
                    onClick={() => videoFileRef.current?.click()}
                    disabled={videoUploading}
                    variant="outline"
                    className="rounded-none border-black h-11 px-4"
                  >
                    {videoUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    <span className="ml-2 hidden sm:inline">Upload</span>
                  </Button>
                  {form.video_url && (
                    <Button
                      type="button"
                      data-testid="clear-video-btn"
                      onClick={() => setForm({ ...form, video_url: "" })}
                      variant="outline"
                      className="rounded-none border-black h-11 px-3 hover:bg-[#FF2A00] hover:text-white hover:border-[#FF2A00]"
                    >
                      <Trash className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <input
                  ref={videoFileRef}
                  type="file"
                  accept="video/*"
                  onChange={onPickVideo}
                  className="hidden"
                  data-testid="video-file-input"
                />
                {form.video_url && (
                  <>
                    <div className="text-[11px] uppercase tracking-widest text-[#00A355] font-bold">
                      ✓ {detectVideoKind(form.video_url)} video set
                    </div>
                    <div className="mt-1">
                      <ProductVideo url={form.video_url} />
                    </div>
                  </>
                )}
              </div>

              {/* External purchase link (e.g. Amazon, AliExpress) */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <LinkIcon className="w-4 h-4" /> External purchase link (optional)
                </Label>
                <p className="text-xs text-neutral-500">
                  Paste a link to the same item on Amazon, AliExpress, etc. We&apos;ll auto-fetch a preview image and show it on the product page so customers can click through.
                </p>
                <div className="relative">
                  <LinkIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                  <Input
                    data-testid="external-link-input"
                    type="url"
                    value={form.external_link}
                    onChange={(e) => setForm({ ...form, external_link: e.target.value })}
                    placeholder="https://www.amazon.com/dp/…"
                    className="rounded-none border-black h-11 pl-9"
                  />
                </div>
                <Input
                  data-testid="external-link-label-input"
                  value={form.external_link_label}
                  onChange={(e) => setForm({ ...form, external_link_label: e.target.value })}
                  placeholder="Label shown above the image · e.g. Also on Amazon"
                  maxLength={80}
                  className="rounded-none border-black h-11"
                />
                {editing?.external_link_image && form.external_link === editing.external_link && (
                  <div className="mt-2 flex items-center gap-3">
                    <img
                      src={editing.external_link_image}
                      alt="External preview"
                      className="w-20 h-20 object-cover border border-black"
                    />
                    <div className="text-[11px] uppercase tracking-widest text-[#00A355] font-bold">
                      ✓ Preview image fetched
                    </div>
                  </div>
                )}
                {form.external_link && (!editing || form.external_link !== editing.external_link) && (
                  <div className="text-[11px] text-neutral-500 italic">
                    Preview image will be auto-fetched when you save.
                  </div>
                )}
              </div>
            </form>
            <div className="sticky bottom-0 z-10 bg-white border-t border-neutral-200 px-4 sm:px-6 py-3 flex gap-2 [padding-bottom:max(0.75rem,env(safe-area-inset-bottom))]">
              <Button
                type="button"
                data-testid="cancel-product-btn"
                onClick={() => setOpen(false)}
                variant="outline"
                className="flex-1 sm:flex-none rounded-none border-black h-12"
              >
                Cancel
              </Button>
              <Button
                data-testid="save-product-btn"
                type="submit"
                form="product-form"
                disabled={uploading || videoUploading}
                className="flex-1 rounded-none bg-[#0033FF] hover:bg-[#0022CC] h-12 font-semibold"
              >
                {editing ? "Save changes" : "Create product"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <SellPriceCalculator />

      <div className="border border-neutral-200">
        {products.length === 0 && <div className="p-10 text-center text-neutral-500">No products yet.</div>}
        {products.map((p) => (
          <div key={p.product_id} data-testid={`seller-product-${p.product_id}`} className="grid grid-cols-[56px_1fr_auto] sm:grid-cols-[64px_1fr_auto_auto_auto] gap-3 sm:gap-4 items-center p-3 border-b border-neutral-200 last:border-b-0 min-w-0">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-neutral-100 border border-neutral-200 overflow-hidden shrink-0">
              {p.images?.[0] && <img src={resolveImg(p.images[0])} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="min-w-0">
              <div className="font-display font-bold text-sm sm:text-base truncate">{p.title}</div>
              <div className="text-[11px] sm:text-xs text-neutral-500 truncate">{p.category} · Stock {p.stock}</div>
              {p.external_link && p.external_clicks > 0 && (
                <div data-testid={`external-clicks-${p.product_id}`} className="text-[10px] sm:text-[11px] uppercase tracking-widest text-[#0033FF] font-bold mt-0.5">
                  {p.external_clicks} compare-price click{p.external_clicks === 1 ? "" : "s"}
                </div>
              )}
              <div className="font-display font-black text-sm mt-0.5 sm:hidden">{fmt(p.price)}</div>
            </div>
            <div className="font-display font-black hidden sm:block">{fmt(p.price)}</div>
            <div className="flex items-center gap-1 sm:contents col-span-1">
              <Button data-testid={`edit-product-${p.product_id}`} onClick={() => openEdit(p)} variant="outline" size="icon" className="rounded-none border-black h-9 w-9 sm:h-10 sm:w-10">
                <Pencil className="w-4 h-4" />
              </Button>
              <Button data-testid={`delete-product-${p.product_id}`} onClick={() => del(p)} variant="outline" size="icon" className="rounded-none border-black h-9 w-9 sm:h-10 sm:w-10 hover:bg-[#FF2A00] hover:text-white hover:border-[#FF2A00]">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
